import { useMemo, useState } from "react";
import { Link2, Send } from "lucide-react";
import { toast } from "sonner";
import api, { apiError, resolveAssetUrl } from "@/lib/api";
import { editorialPublicPath, nexusClientUrl } from "@/lib/helpers";
import { availableMonths, buildProductionRows, monthKey, monthLabel, rowDeliverables } from "@/lib/productionTable";
import { PHASES, countBySituation, nextStepLabel, situationLabel, summarizeRows } from "@/lib/productionSummary";
import "./ProducaoResumo.css";

const MONTH_PREFERENCE = "netzer:production-table:month:v1";
const readMonth = () => {
  try { const value = localStorage.getItem(MONTH_PREFERENCE); if (/^\d{4}-(0[1-9]|1[0-2])$/.test(value || "")) return value; } catch {}
  return monthKey();
};
// Sempre os mesmos cinco blocos, na ordem do que pede atenção.
const CHIPS = [
  { key: "atrasado", color: "#F43F5E", label: "Atrasados", short: "Atraso" },
  { key: "hoje", color: "#F59E0B", label: "Hoje e amanhã", short: "Hoje", includes: ["hoje", "amanha"] },
  { key: "cliente", color: "var(--studio-secondary)", label: "Com o cliente", short: "Cliente" },
  { key: "em_dia", color: "#8CC63F", label: "Em dia", short: "Em dia" },
  { key: "sem_ciclo", color: "var(--studio-muted)", label: "Sem ciclo", short: "Sem ciclo" },
];

function PhaseBar({ phase }) {
  if (phase < 0) return null;
  const done = phase >= PHASES.length;
  return <div className="summary-phase" aria-label={done ? "Mês concluído" : `Fase ${phase + 1} de ${PHASES.length}: ${PHASES[phase].label}`}>
    <div className="summary-phase-bar" aria-hidden="true">{PHASES.map((item, index) => <span key={item.key} className={index < phase ? "is-done" : index === phase ? "is-current" : ""} />)}</div>
    <span className="summary-phase-name">{done ? "Concluído" : PHASES[phase].label}</span>
  </div>;
}

export function ProducaoResumo({ clientes = [], ciclos = [], tarefas = [], onOpenCycle, onCreateCycle, onChange, now }) {
  const [month, setMonth] = useState(readMonth);
  const [busy, setBusy] = useState("");
  const [filter, setFilter] = useState("");
  const rows = useMemo(() => buildProductionRows(clientes, ciclos, tarefas, month), [clientes, ciclos, tarefas, month]);
  const groups = useMemo(() => ({ recurring: summarizeRows(rows.recurring, now), projects: summarizeRows(rows.projects, now) }), [rows, now]);
  const counts = countBySituation([...groups.recurring, ...groups.projects]);
  const months = [...new Set([...availableMonths(ciclos), month])].sort();
  const chip = CHIPS.find((item) => item.key === filter);
  const visible = (summary) => !chip || (chip.includes || [chip.key]).includes(summary.situation);

  const changeMonth = (value) => {
    setMonth(value);
    try { localStorage.setItem(MONTH_PREFERENCE, value); } catch {}
  };
  const open = (summary) => {
    if (summary.cycles.length) onOpenCycle?.(summary.next?.ciclo_id || summary.cycles[0].id);
    else onCreateCycle?.(summary.client, month);
  };
  const copyPortal = async (cliente) => {
    try { await navigator.clipboard.writeText(`${window.location.origin}${editorialPublicPath(cliente)}`); toast.success("Link do portal copiado"); }
    catch { toast.error("Não foi possível copiar o link"); }
  };

  // Marca quando o estúdio entregou para aprovação e quando o cliente respondeu.
  const setWaiting = async (cycle, waiting) => {
    if (busy) return;
    setBusy(cycle.id);
    try {
      await api.patch(`/ciclos/${cycle.id}`, { status: waiting ? "aguardando_cliente" : "em_andamento" });
      toast.success(waiting ? "Marcado como enviado ao cliente" : "Cliente respondeu");
      await onChange?.();
    } catch (error) { toast.error(apiError(error)); }
    finally { setBusy(""); }
  };
  const handoff = (summary) => {
    const cycle = summary.waiting || (PHASES[summary.phase]?.key === "aprovar" ? summary.cycles.find((c) => c.etapa === "aprovacao") : null);
    if (!cycle) return null;
    const waiting = !summary.waiting;
    return <button type="button" className="summary-handoff" disabled={busy === cycle.id} data-testid={`summary-handoff-${summary.client.id}`}
      onClick={(event) => { event.stopPropagation(); setWaiting(cycle, waiting); }}>{waiting ? "Enviei ao cliente" : "Cliente respondeu"}</button>;
  };

  const renderRow = (summary) => {
    const { client } = summary;
    const sub = summary.kind === "project" ? summary.cycles[0]?.titulo : rowDeliverables(summary);
    return <div key={summary.key} className="summary-row" data-testid={`summary-row-${summary.key}`} data-situation={summary.situation} onClick={() => open(summary)}>
      <button type="button" className="summary-client" onClick={(event) => { event.stopPropagation(); open(summary); }} aria-label={summary.cycles.length ? `Abrir ${client.empresa}` : `Criar ciclo de ${monthLabel(month)} para ${client.empresa}`}>
        <span className="summary-avatar" aria-hidden="true">
          <span>{client.empresa?.slice(0, 1)}</span>
          {client.logo ? <img src={resolveAssetUrl(client.logo)} alt="" onError={(event) => { event.currentTarget.style.display = "none"; }} /> : null}
        </span>
        <span className="min-w-0"><span className="summary-name">{client.empresa}</span>{sub && sub !== "Não informado" && <span className="summary-sub">{sub}</span>}</span>
      </button>
      <PhaseBar phase={summary.phase} />
      <div className="summary-next">
        {summary.situation === "sem_ciclo"
          ? <button type="button" className="summary-create" onClick={(event) => { event.stopPropagation(); onCreateCycle?.(client, month); }}>Criar ciclo de {monthLabel(month)}</button>
          : summary.next ? <><p title={summary.next.titulo}>{summary.next.titulo}</p><span className="summary-sub">{nextStepLabel(summary.next)}{summary.open > 1 ? ` · +${summary.open - 1} no mês` : ""}</span></>
          : <span className="summary-sub">Nada pendente</span>}
        {handoff(summary)}
      </div>
      <span className={`summary-status is-${summary.situation}`}>{situationLabel(summary)}</span>
      <div className="summary-actions" onClick={(event) => event.stopPropagation()}>
        <a className="summary-icon" href={nexusClientUrl(client)} target="_blank" rel="noopener noreferrer" aria-label={`Abrir ${client.empresa} no Nexus`} title="Calendário no Nexus" data-testid={`summary-nexus-${client.id}`}><Send size={16} /></a>
        {client.editorial_token && <button type="button" className="summary-icon" onClick={() => copyPortal(client)} aria-label={`Copiar link do portal de ${client.empresa}`} title="Copiar link do portal do cliente"><Link2 size={16} /></button>}
      </div>
    </div>;
  };

  return <div className="summary-view" data-testid="production-summary">
    <div className="summary-toolbar">
      <label htmlFor="summary-month" className="sr-only">Mês</label>
      <select id="summary-month" className="summary-month" value={month} onChange={(event) => changeMonth(event.target.value)}>{months.map((value) => <option key={value} value={value}>{monthLabel(value)}</option>)}</select>
      <div className="summary-chips" role="group" aria-label="Filtrar por situação">
        {CHIPS.map((item) => {
          const total = (item.includes || [item.key]).reduce((sum, key) => sum + (counts[key] || 0), 0);
          return <button key={item.key} type="button" className={`summary-chip is-${item.key} ${total ? "" : "is-zero"}`} style={{ "--chip-color": item.color }} aria-pressed={filter === item.key} aria-label={`${total} ${item.label.toLowerCase()}`} data-testid={`summary-chip-${item.key}`} onClick={() => setFilter(filter === item.key ? "" : item.key)}>
            <strong>{total}</strong><span className="full">{item.label}</span><span className="short" aria-hidden="true">{item.short}</span>
          </button>;
        })}
      </div>
    </div>
    {[{ key: "recurring", title: "Clientes recorrentes" }, { key: "projects", title: "Projetos pontuais" }].map((group) => {
      const items = groups[group.key].filter(visible);
      if (!groups[group.key].length && group.key === "projects") return null;
      return <section key={group.key} className="summary-group" aria-label={group.title}>
        <h2>{group.title} <small>{items.length}</small></h2>
        <div className="summary-head" aria-hidden="true"><span>Cliente</span><span>Fase do mês</span><span>Próximo passo</span><span>Situação</span><span /></div>
        {items.map(renderRow)}
        {!items.length && <p className="summary-empty">{filter ? "Nenhum cliente nesta situação." : "Nenhum cliente neste grupo."}</p>}
      </section>;
    })}
  </div>;
}
